/**
 * Phase A5 — /admin/users
 *
 * Search employer/employee users by email fragment and navigate to the
 * detail page for any row. Mirrors AdminSubmissions chrome so the nav +
 * breadcrumb are visually consistent.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    adminLogout, adminMe, adminUsersSearch,
} from "../lib/admin";

import "./Admin.css";
import "./AdminUsers.css";

const SEARCH_DEBOUNCE_MS = 250;

export default function AdminUsers() {
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const debounceRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        adminMe().then(
            (r) => { if (mounted) setMe(r); },
            (e) => {
                if (e?.response?.status === 401) navigate("/admin/login");
            },
        );
        searchInputRef.current && searchInputRef.current.focus();
        return () => { mounted = false; };
    }, [navigate]);

    const runSearch = useCallback(async (query) => {
        if (!query || query.trim().length < 1) {
            setResults([]);
            return;
        }
        setLoading(true);
        setErr("");
        try {
            const res = await adminUsersSearch(query.trim());
            setResults(res.results || []);
        } catch (e) {
            if (e?.response?.status === 401) {
                navigate("/admin/login");
                return;
            }
            setErr("Search failed. Try again.");
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(q), SEARCH_DEBOUNCE_MS);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [q, runSearch]);

    const onSignOut = async () => {
        try { await adminLogout(); } catch { /* no-op */ }
        navigate("/admin/login");
    };

    const fmt = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                month: "short", day: "numeric", year: "numeric",
            });
        } catch { return iso; }
    };

    return (
        <div className="admin-page" data-testid="admin-users-page">
            <nav className="admin-nav">
                <div style={{ display: "flex", alignItems: "center", gap: "2rem", flexWrap: "wrap" }}>
                    <div className="logo-mark" onClick={() => navigate("/")}>
                        Laguna<span>.</span>
                    </div>
                    <div className="admin-bread">
                        Admin<span className="current">Users</span>
                    </div>
                    <div className="admin-subnav">
                        <button
                            type="button"
                            onClick={() => navigate("/admin/submissions")}
                            data-testid="admin-subnav-submissions"
                        >
                            Submissions
                        </button>
                        <button
                            type="button"
                            className="active"
                            data-testid="admin-subnav-users"
                        >
                            Users
                        </button>
                    </div>
                </div>
                <div className="admin-session">
                    <span>{me?.email}</span>
                    <button
                        className="signout"
                        onClick={onSignOut}
                        data-testid="admin-signout"
                    >
                        Sign out
                    </button>
                </div>
            </nav>

            <div className="admin-wrap">
                <div className="section-kicker">Account actions</div>
                <h1 className="admin-h1">Users <em>directory.</em></h1>
                <p className="admin-sub">
                    Search by email, click a row to send a reset email or
                    generate a temporary password. Every action is logged
                    permanently.
                </p>

                <div className="admin-users-search">
                    <input
                        ref={searchInputRef}
                        type="search"
                        className="admin-users-search-input"
                        placeholder="Search users by email…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="admin-users-search-input"
                        autoComplete="off"
                    />
                </div>

                {err && (
                    <div className="admin-users-error" data-testid="admin-users-error">
                        {err}
                    </div>
                )}

                <div className="admin-users-results">
                    {loading && q.trim().length > 0 && (
                        <div className="admin-users-status" data-testid="admin-users-loading">
                            Searching…
                        </div>
                    )}
                    {!loading && q.trim().length > 0 && results.length === 0 && !err && (
                        <div className="admin-users-status" data-testid="admin-users-empty">
                            No users match that email.
                        </div>
                    )}
                    {results.length > 0 && (
                        <table className="admin-users-table" data-testid="admin-users-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Password</th>
                                    <th>Last sign-in</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r) => (
                                    <tr
                                        key={`${r.role}-${r.id}`}
                                        onClick={() => navigate(`/admin/users/${r.role}/${r.id}`)}
                                        data-testid={`admin-users-row-${r.id}`}
                                    >
                                        <td>{r.email}</td>
                                        <td className="admin-users-role">{r.role}</td>
                                        <td>
                                            {r.has_password ? (
                                                <span className="admin-badge ok">Set</span>
                                            ) : (
                                                <span className="admin-badge muted">Not set</span>
                                            )}
                                        </td>
                                        <td>{fmt(r.last_signin_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
